import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';
import { getAuthToken } from 'auth/utils';
import { useEffect, useRef } from 'react';
import { PrivateRouter } from 'routes/PrivateRouter';
import { PublicRouter } from 'routes/PublicRouter';

import { GlobalLoader } from 'components/loading/GlobalLoader';

import { identifyUser } from 'utils/analytics';

import { useAuthStore, useTokenStore } from 'store/index';

interface StytchCustomClaims {
  name?: string;
  email_id?: string;
}

export function Routes() {
  const { user, loading, initialized, setUser } = useAuthStore();
  const { accessToken } = useTokenStore();

  const { session } = useStytchMemberSession();
  const stytchClient = useStytchB2BClient(); // Add this
  const hasIdentifiedUserRef = useRef(false);

  // Handle session changes and user sync
  useEffect(() => {
    if (session) {
      const authToken = getAuthToken(stytchClient);
      const claims = session.custom_claims as StytchCustomClaims | undefined;
      const email = claims?.email_id ?? '';
      const displayName = claims?.name ?? '';

      if (!email) return;

      // Only update user if the data has actually changed to prevent infinite loops
      const shouldUpdateUser =
        !user ||
        user.email !== email ||
        user.displayName !== displayName ||
        user.uid !== session.member_id;

      if (shouldUpdateUser) {
        const userObj = {
          displayName,
          email,
          photoURL: null, // Set to null so Avatar falls back to initials
          uid: session.member_id || '',
          emailVerified: true,
          isAnonymous: false,
          phoneNumber: '',
          providerData: [],
          accessToken: authToken,
          demo: false,
        };

        setUser(userObj);
      }

      const REFRESH_TOKEN_EXPIRATION_TIME = 7 * 24 * 60 * 60; // 7 days
      const ACCESS_TOKEN_EXPIRATION_TIME = 300; // Default to 5 minutes if not provided

      useTokenStore
        .getState()
        .setAccessToken(authToken || '', ACCESS_TOKEN_EXPIRATION_TIME);
      useTokenStore
        .getState()
        .setRefreshToken(authToken || '', REFRESH_TOKEN_EXPIRATION_TIME);
    }

    if (!session && user) {
      // The session has expired, so we need to logout the user
      useAuthStore.getState().logout();

      // Clear all stored data
      localStorage.clear();
      sessionStorage.clear();
    }
  }, [session, stytchClient, setUser, user]);

  // Handle user identification separately to avoid coupling with user state updates
  useEffect(() => {
    if (!hasIdentifiedUserRef.current && initialized && user) {
      identifyUser(user);
      hasIdentifiedUserRef.current = true;
    } else if (!user) {
      hasIdentifiedUserRef.current = false;
    }
  }, [initialized, user]);

  // Show loader while initializing
  if (!initialized || loading) {
    return <GlobalLoader loading />;
  }

  // After initialization, show the appropriate router
  return (
    <div className="h-full w-full">
      {accessToken && user ? <PrivateRouter /> : <PublicRouter />}
    </div>
  );
}
